import {
  listCoachingApplicationsOptions,
  updateCoachingApplicationStatusFn,
} from "@/actions/coaching-application/handler";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { ArrowLeft, Inbox } from "lucide-react";
import { toast } from "sonner";

const STATUSES = {
  new: "Yeni",
  contacted: "İletişime Geçildi",
  qualified: "Uygun",
  closed: "Kapatıldı",
} as const;

type ApplicationStatus = keyof typeof STATUSES;

export const Route = createFileRoute("/_app/admin/applications")({
  beforeLoad: async ({ context }) => {
    if (!context.user || context.user.role !== "admin") {
      throw redirect({ to: "/" });
    }
  },
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(listCoachingApplicationsOptions()),
  component: CoachingApplicationsPage,
});

function CoachingApplicationsPage() {
  const queryClient = useQueryClient();
  const { data } = useSuspenseQuery(listCoachingApplicationsOptions());
  const applications = data.body;

  const updateStatus = useMutation({
    mutationFn: updateCoachingApplicationStatusFn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: listCoachingApplicationsOptions().queryKey,
      });
      toast.success("Başvuru durumu güncellendi");
    },
    onError: () => toast.error("Başvuru durumu güncellenemedi"),
  });

  return (
    <div className="container mx-auto max-w-7xl space-y-6 px-4 py-24 sm:px-6 lg:px-8">
      <Button asChild variant="ghost" className="w-fit">
        <Link to="/admin">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Admin Paneli
        </Link>
      </Button>

      <div>
        <h1 className="font-bold text-3xl tracking-tight">
          Koçluk Başvuruları
        </h1>
        <p className="mt-1 text-muted-foreground">
          Başvuruları, iletişim bilgilerini ve kampanya kaynaklarını yönetin.
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          {applications.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
              <Inbox className="h-10 w-10 text-muted-foreground" />
              <p className="font-medium">Henüz koçluk başvurusu yok</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tarih / Kişi</TableHead>
                  <TableHead>İletişim</TableHead>
                  <TableHead>Başvuru</TableHead>
                  <TableHead>Kaynak</TableHead>
                  <TableHead>Durum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {applications.map((application) => (
                  <TableRow key={application.applicationId}>
                    <TableCell className="min-w-44 whitespace-normal align-top">
                      <p className="font-medium">{application.name}</p>
                      <p className="text-muted-foreground text-xs">
                        {format(
                          new Date(application.createdAt),
                          "d MMM yyyy HH:mm",
                          {
                            locale: tr,
                          },
                        )}
                      </p>
                    </TableCell>
                    <TableCell className="min-w-52 whitespace-normal align-top">
                      <a
                        className="block hover:underline"
                        href={`mailto:${application.email}`}
                      >
                        {application.email}
                      </a>
                      <a
                        className="text-muted-foreground hover:underline"
                        href={`tel:${application.phone}`}
                      >
                        {application.phone}
                      </a>
                    </TableCell>
                    <TableCell className="min-w-72 whitespace-normal align-top">
                      {application.packageTitle && (
                        <Badge variant="secondary" className="mb-2">
                          {application.packageTitle}
                        </Badge>
                      )}
                      <p className="font-medium text-sm">{application.goal}</p>
                      <p className="mt-1 text-muted-foreground text-xs">
                        Deneyim: {application.experience}
                      </p>
                      {application.message && (
                        <p className="mt-2 text-muted-foreground text-sm">
                          {application.message}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="min-w-40 whitespace-normal align-top">
                      <p className="font-medium">
                        {application.source || "Doğrudan"}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {[application.medium, application.campaign]
                          .filter(Boolean)
                          .join(" / ") || "—"}
                      </p>
                    </TableCell>
                    <TableCell className="align-top">
                      <Select
                        value={application.status as ApplicationStatus}
                        onValueChange={(status: ApplicationStatus) =>
                          updateStatus.mutate({
                            data: {
                              applicationId: application.applicationId,
                              status,
                            },
                          })
                        }
                        disabled={updateStatus.isPending}
                      >
                        <SelectTrigger className="w-44">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUSES).map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
